  
import {ReactElement, useCallback, useEffect, useRef, useState} from 'react';

import useEmblaCarousel, {UseEmblaCarouselType} from 'embla-carousel-react';
import ReactPlayer from 'react-player';

  
import {Box, CardMedia, Grid, useTheme} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';

 
import {CarouselButtonContainer} from './CarouselButtonContainer';
import {isUrlValid} from '../../utils/urlUtils/isUrlValid';

export interface MediaResource {
  id: string;
  mediaType: 'img' | 'video';
  url: string;
}

interface Props {
  mediaResources: MediaResource[];
}

export const MediaCarousel = ({mediaResources}: Props) => {
  const [emblaRef, emblaApi]: UseEmblaCarouselType = useEmblaCarousel();
  const [currentIndex, setCurrentIndex] = useState(0);
  const theme = useTheme();
  const isXsmall = useMediaQuery(theme.breakpoints.down(600));
  const [slides, setSlides] = useState<ReactElement[]>([]);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [maxWidth, setMaxWidth] = useState(1200);
  const [validResources, setValidResources] = useState<MediaResource[]>([]);

  useEffect(() => {
    setValidResources(mediaResources.filter((entry) => isUrlValid(entry.url)));
  }, [mediaResources]);

  useEffect(() => {
    const createSlide = (source: MediaResource) => {
      if (source.mediaType === 'img') {
        return (
          <CardMedia
            key={source.id}
            src={source.url}
            component='img'
            sx={{
              height: {xs: '130px', sm: '230px', md: '230px'},
              width: {xs: '251px', sm: '448px', md: '448px'},
              borderRadius: '16px',
            }}
          />
        );
      }

      return (
        <ReactPlayer
          key={source.id}
          width={isXsmall ? 251 : 448}
          height={isXsmall ? 130 : 230}
          style={{borderRadius: '16px'}}
          url={source.url}
          controls={true}
          light
        />
      );
    };
    if (validResources.length === 1) {
      // Sandwiching with dummy slides makes no sense with one item.
      setSlides([...validResources.map(createSlide)]);
      setCurrentIndex(0);
    } else {
      // Dummy slides are blank slides sandwiching content, so that the carousel functions
      setSlides([
        <DummySlide key={'dummy-slide-start'} />,
        ...validResources.map(createSlide),
        <DummySlide key={'dummy-slide-end'} />,
      ]);
      setCurrentIndex(1);
      emblaApi && emblaApi.scrollTo(1);
    }
  }, [validResources, isXsmall, emblaApi]);

  useEffect(() => {
    emblaApi && emblaApi.reInit();
    if (slides.length > 1) {
      setCurrentIndex(1);
      emblaApi && emblaApi.scrollTo(1);
    }
  }, [slides, emblaApi]);

  const onDivResize = useCallback(() => {
    // Need to reset maxWidth of the embla container dynamically to the actual width of the screen or else embla
    // container will always be set to the max value of `maxWidth`.
    if (containerRef.current?.offsetWidth) {
      setMaxWidth(containerRef.current.offsetWidth);
      emblaApi && emblaApi.reInit();
    }
  }, [emblaApi, setMaxWidth]);
  useEffect(() => {
    window.addEventListener('resize', onDivResize);

    return () => {
      window.removeEventListener('resize', onDivResize);
    };
  }, [onDivResize]);

  useEffect(() => {
    if (containerRef.current?.offsetWidth) {
      setMaxWidth(containerRef.current.offsetWidth);
      emblaApi && emblaApi.reInit();
    }
  }, [emblaApi, setMaxWidth]);

  const updateIndex = useCallback(
    (idx: number) => {
      setCurrentIndex(idx);
    },
    [setCurrentIndex]
  );
  const onScrollListener = useCallback(
    (api: UseEmblaCarouselType[1]) => {
      if (api) {
        // Below logic prevents focusing on the two dummy slides that sandwich media cards
        const nextSlide = api.selectedScrollSnap();
        if (nextSlide === 0 && slides.length > 1) {
          api.scrollTo(1);
          updateIndex(1);
        } else if (nextSlide === slides.length - 1 && slides.length > 1) {
          api.scrollTo(nextSlide - 1);
          updateIndex(nextSlide - 1);
        } else {
          updateIndex(nextSlide);
        }
      }
    },
    [updateIndex, slides]
  );
  useEffect(() => {
    if (emblaApi) {
      emblaApi.on('scroll', onScrollListener);
    }
    return () => {
      emblaApi?.off('scroll', onScrollListener);
    };
  }, [emblaApi, onScrollListener]);

  const jumpToSpecificIndex = useCallback(
    (idx: number) => {
      if (emblaApi) {
        emblaApi.scrollTo(idx);
      }
    },
    [emblaApi]
  );

  const onNextClick = useCallback(() => {
    jumpToSpecificIndex(currentIndex + 1);
  }, [jumpToSpecificIndex, currentIndex]);

  const onPreviousClick = useCallback(() => {
    jumpToSpecificIndex(currentIndex - 1);
  }, [jumpToSpecificIndex, currentIndex]);

  const DummySlide = () => (
    <Box
      minWidth={{xs: '170px', sm: '250px', md: '448px'}}
      sx={{
        minHeight: {xs: '130px', md: '230px'},
        borderRadius: '16px',
        backgroundColor: 'transparent',
      }}
    />
  );

  return (
    <Grid minHeight='400px' width='100%' data-testid='media-carousel'>
      <Grid item paddingBottom='16px' md={12} ref={containerRef}>
        <Grid
          container
          height={{xs: '130px', sm: '230px', md: '230px'}}
          maxWidth={maxWidth}
          sx={{overflow: 'hidden', borderRadius: '16px', paddingBottom: '16px'}}
          className='embla'
          ref={emblaRef}
        >
          <Grid display='flex' justifyContent='flex-start' maxWidth={maxWidth} gap='32px' className='embla__container'>
            {slides.map((slide, idx) => (
              <Box
                key={`slide-${idx}`}
                className='embla__slide'
                sx={{height: {xs: '130px', sm: '230px', md: '230px'}, width: {xs: '251px', sm: '448px', md: '448px'}}}
              >
                {slide}
              </Box>
            ))}
          </Grid>
        </Grid>
      </Grid>
      <CarouselButtonContainer
        onNextClick={onNextClick}
        onCounterClick={jumpToSpecificIndex}
        total={slides.length}
        currentIndex={currentIndex}
        onPreviousClick={onPreviousClick}
      />
    </Grid>
  );
};
